import { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { Container, IconButton, Tooltip, Box } from "@material-ui/core";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import { makeStyles } from "@material-ui/core/styles";
import DataTable, { TableColumn } from "react-data-table-component";
import { MdAdd } from "react-icons/md";

import { useAppDispatch, useAppSelector } from "../../hooks";
import { history } from "../..";
import PageHero from "../shared/page-hero";
import { TabPanel } from "../shared/tab-panel";
import { a11yProps } from "../../utils/constants";
import { tableCustomStyles } from "../../utils/table-styles";
import PaginatedTableTop, {
  paginationTopClassName,
} from "../shared/paginated-table-top";
import {
  clearMaterial,
  clearMaterialType,
  getMaterials,
  getMaterialTypes,
  setMaterials,
  setMaterialTypes,
} from "../../store/scaleStore";
import { Material } from "../../interface/api/Material";
import scaleService from "../../services/scaleService";
import { SetMaterialsAction } from "../../interface/redux-types/SetMaterialsAction";
import DeleteDialog from "../shared/dialog/delete";
import useTimeout from "../../hooks/useTimeout";
import { ActionIconGroup, DeleteIcon, EditIcon } from "../shared/icons";
import { MaterialType } from "../../interface/api/MaterialType";
import { SetMaterialTypesAction } from "../../interface/redux-types/SetMaterialTypesAction";

const useStyles = makeStyles({
  addIconButton: {
    border: "1px solid rgba(0, 0, 0, 0.23)",
  },
});

const MaterialsView = ({}) => {
  const classes = useStyles();

  const materials = useAppSelector(getMaterials);
  const materialTypes = useAppSelector(getMaterialTypes);
  const dispatch = useAppDispatch();
  const [openDeleteMaterialItem, setOpenDeleteMaterialItem] = useState(false);
  const [openDeleteMaterialTypeItem, setOpenDeleteMaterialTypeItem] =
    useState(false);
  const [selectedMaterialItem, setSelectedMaterialItem] = useState<
    Partial<Material>
  >({});
  const [selectedMaterialTypeItem, setSelectedMaterialTypeItem] = useState<
    Partial<MaterialType>
  >({});

  const onOpenDeleteMaterialItem = () => setOpenDeleteMaterialItem(true);
  const onCloseDeleteMaterialItem = () => setOpenDeleteMaterialItem(false);
  const onOpenDeleteMaterialTypeItem = () =>
    setOpenDeleteMaterialTypeItem(true);
  const onCloseDeleteMaterialTypeItem = () =>
    setOpenDeleteMaterialTypeItem(false);

  useEffect(() => {
    dispatch(clearMaterialType);
    dispatch(clearMaterial);
  }, []);

  useTimeout(() => dispatch(clearMaterial()), 100);

  useMemo(() => {
    scaleService.getMaterials().then((response) => {
      if (response.ok) {
        const materials = response.data as Material[];
        const setMaterialsAction: SetMaterialsAction = {
          materials,
        };
        dispatch(setMaterials(setMaterialsAction));
      }
    });

    scaleService.getMaterialTypes().then((response) => {
      if (response.ok) {
        const materialTypes = response.data as MaterialType[];
        const setMaterialTypesAction: SetMaterialTypesAction = {
          materialTypes,
        };
        dispatch(setMaterialTypes(setMaterialTypesAction));
      }
    });
  }, []);

  const onConfirmDeleteMaterialItem = () => {
    if (selectedMaterialItem && selectedMaterialItem.id) {
      scaleService.deleteMaterial(selectedMaterialItem.id).then((response) => {
        if (response.ok) {
          const setMaterialsAction: SetMaterialsAction = {
            materials: materials.filter(
              (m) => m.id !== selectedMaterialItem.id
            ),
          };
          dispatch(setMaterials(setMaterialsAction));
        }
      });
    }

    onCloseDeleteMaterialItem();
  };

  const onConfirmDeleteMaterialTypeItem = () => {
    if (selectedMaterialTypeItem && selectedMaterialTypeItem.id) {
      scaleService
        .deleteMaterialType(selectedMaterialTypeItem.id)
        .then((response) => {
          if (response.ok) {
            const setMaterialTypesAction: SetMaterialTypesAction = {
              materialTypes: materialTypes.filter(
                (m) => m.id !== selectedMaterialTypeItem.id
              ),
            };
            dispatch(setMaterialTypes(setMaterialTypesAction));
          }
        });
    }

    onCloseDeleteMaterialTypeItem();
  };

  const [tab, setTab] = useState(0);

  const materialColumns: TableColumn<Material>[] = [
    { name: "ID", selector: (row) => row.id, sortable: true, width: "100px" },
    { name: "Name", selector: (row) => row.name, sortable: true },
    {
      name: "Actions",
      width: "120px",
      cell: (row) => (
        <ActionIconGroup spread>
          <EditIcon onClick={() => history.push(`/material/${row.id}`)} />
          <DeleteIcon
            onClick={() => {
              setSelectedMaterialItem(row);
              onOpenDeleteMaterialItem();
            }}
          />
        </ActionIconGroup>
      ),
    },
  ];

  const materialTypeColumns: TableColumn<MaterialType>[] = [
    { name: "ID", selector: (row) => row.id, sortable: true, width: "100px" },
    { name: "Name", selector: (row) => row.name, sortable: true },
    {
      name: "Actions",
      width: "120px",
      cell: (row) => (
        <ActionIconGroup spread>
          <EditIcon onClick={() => history.push(`/material-type/${row.id}`)} />
          <DeleteIcon
            onClick={() => {
              setSelectedMaterialTypeItem(row);
              onOpenDeleteMaterialTypeItem();
            }}
          />
        </ActionIconGroup>
      ),
    },
  ];

  return (
    <>
      <Container maxWidth="xl">
        <PageHero
          title="Materials"
          subtitle="Add and Manage Materials."
          showBackButton={false}
        />

        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            indicatorColor="primary"
            textColor="primary"
          >
            <Tab label="Materials" {...a11yProps(0)} />
            <Tab label="Material Types" {...a11yProps(1)} />
          </Tabs>
          <Tooltip title={tab === 0 ? "Add Material" : "Add Material Type"}>
            <IconButton
              color="primary"
              className={classes.addIconButton}
              onClick={() => {
                if (tab === 0) {
                  dispatch(clearMaterial());
                  history.push(`/material/new`);
                } else {
                  dispatch(clearMaterialType());
                  history.push(`/material-type/new`);
                }
              }}
            >
              <MdAdd size={24} />
            </IconButton>
          </Tooltip>
        </Box>

        <TabPanel value={tab} index={0}>
          <Box mt={2}>
            <PaginatedTableTop>
              <DataTable
                className={paginationTopClassName}
                columns={materialColumns}
                data={materials ?? []}
                customStyles={tableCustomStyles}
                striped
                highlightOnHover
                responsive
                pagination
                paginationPerPage={25}
                paginationRowsPerPageOptions={[25, 50, 100, 200]}
              />
            </PaginatedTableTop>
          </Box>
        </TabPanel>

        <TabPanel value={tab} index={1}>
          <Box mt={2}>
            <PaginatedTableTop>
              <DataTable
                className={paginationTopClassName}
                columns={materialTypeColumns}
                data={materialTypes ?? []}
                customStyles={tableCustomStyles}
                striped
                highlightOnHover
                responsive
                pagination
                paginationPerPage={25}
                paginationRowsPerPageOptions={[25, 50, 100, 200]}
              />
            </PaginatedTableTop>
          </Box>
        </TabPanel>
      </Container>
      <DeleteDialog
        open={openDeleteMaterialItem}
        onClose={onCloseDeleteMaterialItem}
        onConfirmDelete={onConfirmDeleteMaterialItem}
      />
      <DeleteDialog
        open={openDeleteMaterialTypeItem}
        onClose={onCloseDeleteMaterialTypeItem}
        onConfirmDelete={onConfirmDeleteMaterialTypeItem}
      />
    </>
  );
};

export default connect()(MaterialsView);
